<template>
  <div class="quote-estimator">
    <div class="page-header no-print">
      <div class="header-left">
        <h1 class="page-title">報價管理</h1>
        <p v-if="activeTab !== 'estimate'" class="page-subtitle">
          表價來源：經銷商版 v{{ catalog.version }}
        </p>
        <p v-else class="page-subtitle">
          表價來源：經銷商版 v{{ catalog.version }}
          <span v-if="quote.isDeviated" class="badge-deviated">已偏離表價</span>
        </p>
        <div v-if="activeTab === 'estimate' && savedQuote" class="version-switch">
          <span class="version-switch__no">{{ savedQuote.quoteNo }}</span>
          <span class="version-switch__label">查看版本</span>
          <button
            v-for="version in quoteVersions"
            :key="version.versionNo"
            type="button"
            class="version-chip"
            :class="{ active: isViewingVersion(version.versionNo) }"
            :title="formatSavedAt(version.createdAt)"
            @click="openSavedQuote(savedQuote.id, version.versionNo, { keepSubTab: true })"
          >
            第 {{ version.versionNo }} 版
          </button>
          <span v-if="viewingHistoricalQuote" class="version-switch__hint history-alert">此版為歷史報價，非成交報價。成交內容在第 {{ dealVersionNo }} 版。</span>
          <span v-else-if="quoteWon" class="version-switch__hint won-alert">此報價已成交（{{ invoicePartyLabel(savedQuote.invoiceParty) }}），不能再修改或刪除。發票依此開立。</span>
          <span v-else-if="!isViewingLatest" class="version-switch__hint">
            正在查看第 {{ savedQuote.viewingVersion }} 版（唯讀）。只有第 {{ savedQuote.currentVersion }} 版可以修改。
          </span>
        </div>
      </div>
      <div v-if="activeTab === 'saved'" class="header-actions">
        <button type="button" class="btn btn-secondary" @click="activeTab = 'tiers'">
          好友級距表
        </button>
        <button type="button" class="btn btn-primary" @click="resetForm">
          新增報價單
        </button>
      </div>
      <div v-else-if="activeTab === 'tiers'" class="header-actions">
        <button type="button" class="btn btn-secondary" @click="backToList">
          返回列表
        </button>
      </div>
      <div v-else class="header-actions">
        <button type="button" class="btn btn-secondary" @click="backToList">
          返回列表
        </button>
        <button
          v-if="!savedQuote"
          type="button"
          class="btn btn-secondary"
          :disabled="!canSave || quoteSaving"
          @click="saveQuote"
        >
          {{ quoteSaving ? '儲存中…' : '存檔' }}
        </button>
        <button
          v-else-if="!quoteWon"
          type="button"
          class="btn btn-secondary"
          :disabled="!canSave || quoteSaving || formLocked"
          :title="formLocked ? '舊版報價無法修改，請切換到最新版本' : ''"
          @click="saveQuoteVersion"
        >
          {{ quoteSaving ? '儲存中…' : '另存版本' }}
        </button>
        <button
          v-if="savedQuote && !quoteWon"
          type="button"
          class="btn btn-secondary"
          :disabled="quoteSaving"
          @click="markQuoteWon"
        >
          設為已成交
        </button>
        <button
          v-if="savedQuote && !quoteWon"
          type="button"
          class="btn btn-danger"
          :disabled="quoteSaving"
          @click="deleteSavedQuote(savedQuote.id)"
        >
          刪除
        </button>
      </div>
    </div>

    <div v-show="activeTab === 'saved'" class="panel saved-panel no-print">
      <div class="saved-toolbar">
        <div class="search-box">
          <input
            v-model.trim="savedSearch"
            type="text"
            class="form-control"
            placeholder="搜尋單號、客戶或方案"
            @input="onSavedSearch"
          />
        </div>
      </div>
      <p v-if="savedError" class="form-hint">{{ savedError }}</p>
      <p v-else-if="savedLoading" class="form-hint">載入中…</p>
      <div v-else class="table-wrapper">
        <table class="saved-table">
          <thead>
            <tr>
              <th>報價單號</th>
              <th>客戶</th>
              <th>方案</th>
              <th>版本</th>
              <th class="num">含稅總計</th>
              <th>狀態</th>
              <th>更新時間</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="savedQuotes.length === 0">
              <td colspan="8" class="empty-cell">尚未建立報價單</td>
            </tr>
            <tr v-for="item in savedQuotes" :key="item.id">
              <td>{{ item.quoteNo }}</td>
              <td>{{ item.customerName }}</td>
              <td>{{ item.planName || '—' }}</td>
              <td>第 {{ item.currentVersion }} 版</td>
              <td class="num">{{ formatMoneyOrDash(item.latestTotalInclTax) }}</td>
              <td>
                <span v-if="item.status === 'won'" class="badge-won">已成交・第 {{ item.wonVersion || item.currentVersion }} 版・{{ invoicePartyLabel(item.invoiceParty) }}</span>
                <span v-else>未成交</span>
              </td>
              <td>{{ formatSavedAt(item.updatedAt) }}</td>
              <td class="saved-actions">
                <div class="action-buttons">
                  <button
                    type="button"
                    class="btn btn-sm btn-primary"
                    @click="openSavedQuote(item.id, item.currentVersion)"
                  >
                    <i class="ri-edit-line"></i>
                    編輯
                  </button>
                  <button
                    v-if="item.status !== 'won'"
                    type="button"
                    class="btn btn-sm btn-danger"
                    @click="deleteSavedQuote(item.id)"
                  >
                    刪除
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="savedTotalPages > 1" class="saved-pager">
        <button type="button" class="btn btn-sm btn-secondary" :disabled="savedPage <= 1" @click="changeSavedPage(savedPage - 1)">上一頁</button>
        <span>{{ savedPage }} / {{ savedTotalPages }}</span>
        <button type="button" class="btn btn-sm btn-secondary" :disabled="savedPage >= savedTotalPages" @click="changeSavedPage(savedPage + 1)">下一頁</button>
      </div>
    </div>

    <div v-show="activeTab === 'tiers'" class="panel tiers-panel no-print">
      <h2 class="panel-title">企業版好友級距表</h2>
      <p class="tiers-intro">
        計價數量取有效好友數與會員數之高者；有效好友數 = LINE 追蹤者數 − 封鎖數（所有官方帳號合計）。
        年繳 = 月費 × 10。超過 {{ formatFriends(enterprisePlan?.projectQuoteAboveFriends) }} 為專案報價。
      </p>
      <div class="table-wrapper">
        <table class="tiers-table">
          <thead>
            <tr>
              <th>計價數量上限</th>
              <th class="num">基本月費</th>
              <th class="num">好友級距加價</th>
              <th class="num">月費合計（未稅）</th>
              <th class="num">年繳（未稅）</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in enterpriseTierRows" :key="row.maxFriends">
              <td>{{ row.rangeLabel }}</td>
              <td class="num">{{ formatMoney(row.baseMonthly) }}</td>
              <td class="num">{{ formatMoney(row.friendsSurcharge) }}</td>
              <td class="num">{{ formatMoney(row.monthlyPrice) }}</td>
              <td class="num">{{ formatMoney(row.yearlyPrice) }}</td>
            </tr>
            <tr>
              <td>&gt;{{ formatFriends(enterprisePlan?.projectQuoteAboveFriends) }}</td>
              <td class="num" colspan="4">專案報價（請自填月費）</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="tiers-note">
        表價來源：經銷商版 v{{ catalog.version }}。估價時企業版會拆成「基本月費」與「好友級距加價」兩筆。
      </p>
    </div>

    <div
      v-show="activeTab === 'estimate'"
      class="estimator-layout"
      :class="{ 'estimator-layout--preview': estimateSubTab === 'preview' }"
    >
      <section class="panel form-panel" :class="{ 'is-locked': formLocked }">
        <div class="sub-tab-nav">
          <button
            type="button"
            class="sub-tab-btn"
            :class="{ active: estimateSubTab === 'share' }"
            @click="estimateSubTab = 'share'"
          >
            經銷價參數
          </button>
          <button
            type="button"
            class="sub-tab-btn"
            :class="{ active: estimateSubTab === 'plan' }"
            @click="estimateSubTab = 'plan'"
          >
            方案與用量
          </button>
          <button
            type="button"
            class="sub-tab-btn"
            :class="{ active: estimateSubTab === 'customer' }"
            @click="estimateSubTab = 'customer'"
          >
            客戶資訊
          </button>
          <button
            type="button"
            class="sub-tab-btn"
            :class="{ active: estimateSubTab === 'preview' }"
            @click="estimateSubTab = 'preview'"
          >
            預覽報價單
          </button>
        </div>

        <p v-if="viewingHistoricalQuote" class="history-alert">
          此版為歷史報價，非成交報價。成交內容在第 {{ dealVersionNo }} 版。
        </p>
        <p v-else-if="quoteWon" class="won-alert">
          此報價已成交（{{ invoicePartyLabel(savedQuote.invoiceParty) }}），不能再修改或刪除。發票依此開立。
        </p>
        <p v-else-if="formLocked" class="form-lock-notice">
          第 {{ savedQuote.viewingVersion }} 版已封存，無法修改。請切換到第 {{ savedQuote.currentVersion }} 版後再編輯。
        </p>

        <fieldset v-show="estimateSubTab !== 'preview'" class="form-fieldset" :disabled="formLocked">
        <div v-show="estimateSubTab === 'customer'">
          <h2 class="panel-title">客戶與報價資訊</h2>
          <div class="form-grid">
            <div class="form-group">
              <label class="required">客戶名稱</label>
              <input v-model.trim="form.customerName" type="text" class="form-control" placeholder="公司或品牌名稱" />
            </div>
            <div class="form-group">
              <label>統一編號</label>
              <input
                :value="form.taxId"
                type="text"
                lang="en"
                inputmode="numeric"
                spellcheck="false"
                autocomplete="off"
                class="form-control tax-id-input"
                maxlength="8"
                placeholder="選填，8 碼半形數字"
                @mouseenter="focusAlphanumeric"
                @focus="focusAlphanumeric"
                @compositionstart="markNonAlphanumericIme('taxId')"
                @compositionupdate="markNonAlphanumericIme('taxId')"
                @blur="clearNonAlphanumericIme('taxId')"
                @input="onDigitsInput('taxId', $event, 8)"
              />
              <p v-if="nonAlphanumericIme.taxId" class="form-hint form-hint-error">請切換為英數輸入法</p>
            </div>
            <div class="form-group form-group--full">
              <label>地址</label>
              <input v-model.trim="form.address" type="text" class="form-control" placeholder="選填" />
            </div>
            <div class="form-group">
              <label>聯絡人</label>
              <input v-model.trim="form.contactName" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>電話</label>
              <input
                :value="form.contactPhone"
                type="text"
                inputmode="numeric"
                class="form-control"
                placeholder="半形數字"
                @compositionstart="markNonAlphanumericIme('contactPhone')"
                @compositionupdate="markNonAlphanumericIme('contactPhone')"
                @blur="clearNonAlphanumericIme('contactPhone')"
                @input="onDigitsInput('contactPhone', $event)"
              />
              <p v-if="nonAlphanumericIme.contactPhone" class="form-hint form-hint-error">請切換為英數輸入法</p>
            </div>
            <div class="form-group">
              <label>Email</label>
              <input
                v-model.trim="form.contactEmail"
                type="email"
                class="form-control"
                :class="{ 'is-invalid': emailError }"
                placeholder="name@example.com"
              />
              <p v-if="emailError" class="form-hint form-hint-error">{{ emailError }}</p>
            </div>
            <div class="form-group">
              <label>業務姓名</label>
              <input v-model.trim="form.salespersonName" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>報價日期</label>
              <input v-model="form.quoteDate" type="date" class="form-control" />
            </div>
            <div class="form-group">
              <label>有效期限</label>
              <input v-model="form.validUntil" type="date" class="form-control" />
              <p class="form-hint">
                預設：企業版 {{ resolveQuoteValidDays('enterprise') }} 天，其他方案 {{ resolveQuoteValidDays('basic') }} 天（可手動修改）
              </p>
            </div>
          </div>

          <h2 class="panel-title">備註</h2>
          <textarea
            v-model.trim="form.notes"
            class="form-control"
            rows="3"
            placeholder="僅本次報價有效的條件或說明"
          />
        </div>

        <div v-show="estimateSubTab === 'plan'">
          <h2 class="panel-title">方案與用量</h2>
          <div class="form-grid">
            <div class="form-group">
              <label class="required">方案</label>
              <select v-model="form.planCode" class="form-control" :disabled="plansLoading">
                <option v-if="plansLoading" value="" disabled>載入方案中…</option>
                <option v-for="plan in catalog.plans" :key="plan.code" :value="plan.code">
                  {{ planOptionLabel(plan) }}
                </option>
              </select>
              <p v-if="plansError" class="form-hint">{{ plansError }}</p>
              <p v-else class="form-hint">
                方案名稱、月費與用量上限來自方案管理。建置費金額請在下方產品報價單調整
              </p>
            </div>
            <div class="form-group form-group--full">
              <label>建置服務（擇一）</label>
              <div class="checkbox-list">
                <label class="checkbox-item">
                  <input v-model="form.setupChoice" type="radio" value="standard" />
                  <span>
                    {{ setupFeeTitle(selectedPlan) }}（{{ formatMoney(selectedPlan?.setupFee) }}）
                  </span>
                </label>
                <label class="checkbox-item">
                  <input v-model="form.setupChoice" type="radio" value="premium" />
                  <span>
                    {{ catalog.premiumSetup?.name || '加值版建置服務' }}
                    （{{ formatMoney(catalog.premiumSetup?.price ?? 15000) }}）
                    — {{ catalog.premiumSetup?.description }}
                  </span>
                </label>
              </div>
              <p v-if="form.setupChoice !== 'premium' && selectedPlan?.setupFeeIncludes" class="form-hint">
                內含：{{ selectedPlan.setupFeeIncludes }}
              </p>
              <p v-else class="form-hint">與{{ setupFeeTitle(selectedPlan) }}互斥，已含基本導入服務，不另計導入費／建置費</p>
            </div>
            <div class="form-group">
              <label class="required">有效好友數/會員數（以數量大者為主）</label>
              <input
                v-model.number="form.effectiveFriends"
                type="number"
                min="0"
                class="form-control"
                placeholder="有效好友或會員數，取高者"
              />
              <p class="form-hint">有效好友或會員數，以數量高者計算（有效好友 = 追蹤者 − 封鎖，所有 OA 合計）</p>
            </div>
            <div class="form-group">
              <label>繳費週期</label>
              <select v-model="form.billingCycle" class="form-control">
                <option v-for="cycle in catalog.billingCycles" :key="cycle.code" :value="cycle.code">
                  {{ cycle.name }}
                </option>
              </select>
            </div>
          </div>

          <div v-if="quote.exceedsPlanFriendLimit && quote.upgradeSuggestion" class="alert alert-warning">
            <p>{{ quote.upgradeSuggestion.message }}</p>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="applyUpgradeSuggestion">
              一鍵改報{{ quote.upgradeSuggestion.planName }}
            </button>
          </div>

          <div v-if="quote.requiresCustomEnterprisePrice" class="alert alert-info">
            有效好友超過 200 萬，無表價級距；基本月費已帶出地板價，請於下方報價單「報價（未稅）」欄填寫好友級距／專案加價（方案價格亦可調整）。
          </div>
          <div v-if="quote.requiresCustomPlanPrice" class="alert alert-info">
            此方案月費另議，請於下方報價單「報價（未稅）」欄填寫月費。
          </div>

          <h2 class="panel-title">選購模組（月費）</h2>
          <p class="form-hint module-hint">
            「行銷代操服務」已包含「內容上架服務」，兩者不可同時勾選。
          </p>
          <div class="checkbox-list">
            <label
              v-for="mod in catalog.modules"
              :key="mod.code"
              class="checkbox-item"
              :class="{ 'checkbox-item--disabled': isModuleDisabled(mod.code) }"
            >
              <input
                type="checkbox"
                :value="mod.code"
                :checked="form.moduleCodes.includes(mod.code)"
                :disabled="isModuleDisabled(mod.code)"
                @change="onModuleToggle(mod.code, $event.target.checked)"
              />
              <span>
                {{ mod.name }}（{{ formatMoney(mod.monthlyPrice) }}/月）
                <template v-if="mod.description"> — {{ mod.description }}</template>
                <template v-if="mod.code === 'chatdesk' && mod.extraSeatMonthlyPrice">
                  ；加席 {{ formatMoney(mod.extraSeatMonthlyPrice) }}/席／月
                </template>
              </span>
            </label>
          </div>
          <div v-if="form.moduleCodes.includes('chatdesk')" class="form-grid" style="margin-top: 12px;">
            <div class="form-group">
              <label>
                ChatDesk 加購座席
                （{{ formatMoney(chatdeskModule?.extraSeatMonthlyPrice || 250) }}/席／月）
              </label>
              <input v-model.number="form.chatdeskExtraSeats" type="number" min="0" class="form-control" />
              <p class="form-hint">
                模組已含 {{ chatdeskModule?.includedSeats ?? 3 }} 席；此欄為超出內含的加購席數
              </p>
            </div>
          </div>

          <template v-if="form.planCode === 'enterprise'">
            <h2 class="panel-title">企業版導入加購（單次）</h2>
            <div class="form-grid">
              <div class="form-group">
                <label>
                  LINE OA 增設
                  （{{ formatMoney(enterpriseAddonPrice('extra_line_oa')) }}/個）
                </label>
                <input v-model.number="form.extraLineOa" type="number" min="0" class="form-control" />
              </div>
              <div class="form-group">
                <label>
                  圖文選單追加
                  （{{ formatMoney(enterpriseAddonPrice('extra_rich_menu')) }}/張）
                </label>
                <input v-model.number="form.extraRichMenus" type="number" min="0" class="form-control" />
                <p class="form-hint">多張每個分開計算（數量請填張數）</p>
              </div>
              <div class="form-group">
                <label>
                  教育訓練追加
                  （{{ formatMoney(enterpriseAddonPrice('extra_training')) }}/場）
                </label>
                <input v-model.number="form.extraTrainingSessions" type="number" min="0" class="form-control" />
              </div>
            </div>
          </template>

          <h2 class="panel-title">加購／金流／發票</h2>
          <div class="checkbox-list">
            <label class="checkbox-item">
              <input v-model="form.smsPointsEnabled" type="checkbox" />
              <span>
                加購簡訊點數（{{ formatMoney(catalog.smsPoints?.unitPrice ?? 1) }}/點）
              </span>
            </label>
            <label class="checkbox-item">
              <input v-model="form.aiCreditTopupEnabled" type="checkbox" />
              <span>
                AI 儲值（ChatMate 對話額度；最小單位
                {{ formatMoney(catalog.aiCreditTopup?.minAmount ?? 1000) }}）
              </span>
            </label>
            <label
              v-for="addon in catalog.annualAddons || []"
              :key="addon.code"
              class="checkbox-item"
            >
              <input
                type="checkbox"
                :value="addon.code"
                :checked="form.annualAddonCodes.includes(addon.code)"
                @change="onAnnualAddonToggle(addon.code, $event.target.checked)"
              />
              <span>
                {{ addon.name }}
                <template v-if="Number(addon.yearlyPrice) === 0">（無年費）</template>
                <template v-else>（{{ formatMoney(addon.yearlyPrice) }}/年）</template>
                <template v-if="addon.description && Number(addon.yearlyPrice) !== 0">
                  — {{ addon.description }}
                </template>
              </span>
            </label>
          </div>
          <div
            v-if="form.smsPointsEnabled || form.aiCreditTopupEnabled"
            class="form-grid"
            style="margin-top: 12px;"
          >
            <div v-if="form.smsPointsEnabled" class="form-group">
              <label>加購簡訊點數（{{ formatMoney(catalog.smsPoints?.unitPrice ?? 1) }}/點）</label>
              <input v-model.number="form.smsPointsQty" type="number" min="0" step="1" class="form-control" />
              <p class="form-hint">
                未稅金額 {{ formatMoney((Number(form.smsPointsQty) || 0) * (catalog.smsPoints?.unitPrice || 1)) }}
                ；儲值後依實際發送扣點
              </p>
            </div>
            <div v-if="form.aiCreditTopupEnabled" class="form-group">
              <label>
                AI 儲值金額（未稅，最小 {{ formatMoney(catalog.aiCreditTopup?.minAmount ?? 1000) }}）
              </label>
              <input
                v-model.number="form.aiCreditTopupAmount"
                type="number"
                :min="catalog.aiCreditTopup?.minAmount ?? 1000"
                :step="catalog.aiCreditTopup?.stepAmount ?? 1000"
                class="form-control"
              />
              <p class="form-hint">預付儲值，依實際對話扣抵</p>
            </div>
          </div>

          <h2 class="panel-title">舊會員資料匯入（單次）</h2>
          <label class="checkbox-item">
            <input v-model="form.pointsImportEnabled" type="checkbox" />
            <span>需要會員資料／點數匯入（標準格式免費）</span>
          </label>
          <p class="form-hint">標準 CSV 匯入不收費；非標準格式另收資料清洗處理費。</p>
          <div v-if="form.pointsImportEnabled" class="form-grid">
            <div class="form-group form-group--full">
              <label class="checkbox-item">
                <input v-model="form.pointsNeedsCleaning" type="checkbox" />
                <span>
                  需資料清洗處理（{{ formatMoney(catalog.pointsImport.cleaningFeeFrom) }} 起）
                </span>
              </label>
            </div>
            <div v-if="form.pointsNeedsCleaning" class="form-group">
              <label>資料清洗處理費（未稅，表價起 {{ formatMoney(catalog.pointsImport.cleaningFeeFrom) }}）</label>
              <input v-model.number="form.pointsCleaningFee" type="number" min="0" class="form-control" />
              <p class="form-hint">可依實際調整</p>
            </div>
          </div>

          <h2 class="panel-title">客製報價項目</h2>
          <p class="form-hint">可新增多筆。計入單次費用與首年合計。</p>
          <div
            v-for="item in form.customItems"
            :key="item.id"
            class="custom-item"
          >
            <div class="form-group">
              <label>項目名稱</label>
              <input v-model.trim="item.name" type="text" class="form-control" placeholder="例如：專案客製開發" />
            </div>
            <div class="form-group">
              <label>說明</label>
              <input v-model.trim="item.description" type="text" class="form-control" placeholder="選填，會印在報價單" />
            </div>
            <div class="form-group">
              <label>數量</label>
              <input v-model.number="item.quantity" type="number" min="1" step="1" class="form-control" />
            </div>
            <div class="form-group">
              <label>未稅單價</label>
              <input v-model.number="item.unitPrice" type="number" min="0" class="form-control" />
            </div>
            <div class="custom-item__side">
              <span class="custom-item__sum">{{ formatMoney(customItemAmount(item)) }}</span>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="removeCustomItem(item.id)">
                移除
              </button>
            </div>
          </div>
          <button type="button" class="btn btn-secondary custom-item-add" @click="addCustomItem">
            新增客製項目
          </button>

          <h2 class="panel-title">試算摘要</h2>
          <dl class="summary-list">
            <template v-if="form.planCode === 'enterprise'">
              <div>
                <dt>企業版基本月費</dt>
                <dd>{{ formatMoneyOrDash(quote.enterpriseBaseMonthly) }}</dd>
              </div>
              <p class="renewal-hint enterprise-base-hint">
                {{ enterpriseBaseFeeLabel }}
              </p>
              <div>
                <dt>好友級距加價</dt>
                <dd>{{ formatMoneyOrDash(quote.enterpriseFriendsSurcharge) }}</dd>
              </div>
            </template>
            <div>
              <dt>表價月費（方案＋模組）</dt>
              <dd>{{ formatMoneyOrDash(quote.listMonthlySubscription) }}</dd>
            </div>
            <div>
              <dt>報價月費</dt>
              <dd>{{ formatMoneyOrDash(quote.quotedMonthlySubscription) }}</dd>
            </div>
            <div>
              <dt>本約訂閱應付（{{ quote.billingCycle?.name || '—' }}）</dt>
              <dd>{{ formatMoney(quote.billingPeriodAmount) }}</dd>
            </div>
            <div>
              <dt>單次／加購合計</dt>
              <dd>{{ formatMoney(serviceFeeTotal) }}</dd>
            </div>
            <div>
              <dt>未稅小計</dt>
              <dd>{{ formatMoney(quote.subtotalExclTax) }}</dd>
            </div>
            <div>
              <dt>營業稅 {{ Math.round((catalog.taxRate || 0) * 100) }}%</dt>
              <dd>{{ formatMoney(quote.taxAmount) }}</dd>
            </div>
            <div class="summary-total">
              <dt>首年含稅總計</dt>
              <dd>{{ formatMoney(quote.totalInclTax) }}</dd>
            </div>
          </dl>
          <dl v-if="isDealerSheet" class="summary-list summary-renewal">
            <div>
              <dt>首年售價（未稅）{{ sellingPeriodSuffix }}</dt>
              <dd>{{ formatMoney(dealerShareResult.firstYearBasis) }}</dd>
            </div>
            <div class="summary-total">
              <dt>首年經銷價（未稅）</dt>
              <dd>{{ formatMoney(dealerShareResult.firstYearPayable) }}</dd>
            </div>
          </dl>
          <p v-if="isDealerSheet" class="renewal-hint">{{ dealerShareResult.parameterSummary }}</p>
        </div>

        <div v-show="estimateSubTab === 'share'">
          <h2 class="panel-title">經銷價參數</h2>
          <p class="form-hint">這裡是經銷商留下的比例。經銷價 = 售價 − 分潤。改這裡會重算整張經銷價估價單；單列應付比例可在下方直接改。</p>
          <div class="deal-mode-row">
            <div class="form-group">
              <label>企業版成交模式</label>
              <select
                class="form-control"
                :value="form.dealerShare.enterpriseDeal"
                @change="setEnterpriseDeal($event.target.value)"
              >
                <option value="dealer">經銷商主導（依下方級距，持續分潤）</option>
                <option value="referral">引薦、原廠成交（僅首年介紹費）</option>
              </select>
              <p class="form-hint">引薦介紹費只套在企業版訂閱，非企業版仍用訂閱分潤比例。</p>
            </div>
            <div class="form-group">
              <template v-if="isReferralDeal">
                <label>引薦介紹費（%）</label>
                <input v-model.number="form.dealerShare.referralRate" type="number" min="0" max="100" step="0.1" class="form-control" />
              </template>
            </div>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>訂閱分潤等級</label>
              <select
                class="form-control"
                :value="form.dealerShare.tier"
                :disabled="isReferralDeal"
                @change="setDealerTier($event.target.value)"
              >
                <option value="standard">一般（年營業額未達 100 萬）</option>
                <option value="advanced">進階（年營業額 100 萬以上）</option>
                <option value="custom">自訂比例</option>
              </select>
              <p v-if="isReferralDeal" class="form-hint">引薦模式改用介紹費，不使用訂閱分潤等級。</p>
            </div>
            <div class="form-group">
              <label>訂閱分潤比例（%）</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                class="form-control"
                :value="form.dealerShare.subscriptionRate"
                :disabled="isReferralDeal"
                @input="setSubscriptionRate($event.target.value)"
              />
              <p v-if="!isReferralDeal" class="form-hint">一般 30%、進階 40%。以客戶未稅實收的方案與選購月費計算。</p>
            </div>
            <div class="form-group">
              <label>導入／建置費執行方</label>
              <select v-model="form.dealerShare.setupExecutor" class="form-control">
                <option value="factory">原廠執行（依訂閱分潤級距）</option>
                <option value="dealer">經銷商自行執行</option>
              </select>
              <p class="form-hint">含加值版建置服務。其餘一次性費用預設不分潤。</p>
            </div>
            <div class="form-group">
              <label>自行執行導入分潤（%）</label>
              <input v-model.number="form.dealerShare.setupDealerRate" type="number" min="0" max="100" step="0.1" class="form-control" />
            </div>
          </div>
          <h2 class="panel-title">ChatMate／ChatDesk</h2>
          <p class="form-hint">非引薦時，ChatMate、ChatDesk 與 AI 儲值分潤固定 25%，經銷價為售價的 75%。引薦模式時 ChatMate、ChatDesk、企業版建置費與 AI 儲值改與引薦介紹費相同。加購座席也一併跟著介紹費。</p>
        </div>
        </fieldset>
        <div v-show="estimateSubTab === 'preview'" class="quote-preview">
    <div v-if="activeTab === 'estimate'" class="preview-toolbar no-print">
      <div class="audience-switch">
        <button
          type="button"
          class="audience-switch__btn"
          :class="{ active: !isDealerSheet }"
          :disabled="quoteWon"
          @click="setSheetView('customer')"
        >
          客戶售價
        </button>
        <button
          type="button"
          class="audience-switch__btn"
          :class="{ active: isDealerSheet }"
          :disabled="quoteWon"
          @click="setSheetView('dealer')"
        >
          經銷價
        </button>
        <span v-if="!quoteWon" class="audience-switch__hint">
          同一張報價同時有客戶售價與經銷價，可切換查看明細。成交時再選擇發票開給客戶或經銷商。
        </span>
      </div>
      <div class="preview-actions">
        <button
          type="button"
          class="btn btn-secondary"
          :disabled="!canPrint || docxExporting"
          @click="handleDownloadDocx"
        >
          <i class="ri-file-word-line"></i>
          {{ docxExporting ? '產生中…' : '下載 Word' }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!canPrint"
          @click="handlePrint"
        >
          <i class="ri-printer-line"></i>
          列印／另存 PDF
        </button>
      </div>
    </div>
    <p v-if="!canPrint" class="print-hint no-print">
      <template v-if="emailError">{{ emailError }}</template>
      <template v-else-if="quote.requiresCustomEnterprisePrice">請先於報價單填寫企業版好友級距／專案加價（方案價格可調），並填寫客戶名稱後再列印或下載</template>
      <template v-else-if="quote.requiresCustomPlanPrice">此方案月費另議，請於報價單「報價（未稅）」填寫月費，並填寫客戶名稱後再列印或下載</template>
      <template v-else>請填寫客戶名稱後即可列印或下載報價單</template>
    </p>

    <div v-if="savedQuote && activeTab === 'estimate' && estimateSubTab === 'preview'" class="version-bar no-print">
      <span class="version-switch__label">查看報價版本</span>
      <button
        v-for="version in quoteVersions"
        :key="`sheet-${version.versionNo}`"
        type="button"
        class="version-chip"
        :class="{ active: isViewingVersion(version.versionNo) }"
        :title="formatSavedAt(version.createdAt)"
        @click="openSavedQuote(savedQuote.id, version.versionNo, { keepSubTab: true })"
      >
        第 {{ version.versionNo }} 版
      </button>
      <span class="form-hint" :class="{ 'history-alert': viewingHistoricalQuote, 'won-alert': quoteWon && !viewingHistoricalQuote }">
        <template v-if="viewingHistoricalQuote">此版為歷史報價，非成交報價。</template>
        <template v-else-if="quoteWon">此報價已成交，僅供查看與列印。</template>
        <template v-else-if="formLocked">第 {{ savedQuote.viewingVersion }} 版為舊版，僅供查看與列印。</template>
        <template v-else>目前顯示第 {{ savedQuote.viewingVersion }} 版，可修改後另存為新版本。</template>
      </span>
    </div>

    <!-- 列印報價單（僅估價 tab 顯示；列印時仍輸出） -->
    <section v-if="!isDealerSheet" v-show="activeTab === 'estimate' && estimateSubTab === 'preview'" class="quote-sheet" aria-label="報價單預覽">
      <header class="quote-sheet__header">
        <div>
          <p class="quote-sheet__brand">LOCKLINE+</p>
          <h2 class="quote-sheet__title">產品報價單</h2>
        </div>
        <div class="quote-sheet__meta">
          <p v-if="savedQuote">{{ savedQuote.quoteNo }}　第 {{ savedQuote.viewingVersion }} 版</p>
          <p>報價日期：{{ form.quoteDate || '—' }}</p>
          <p>有效期限：{{ form.validUntil || '—' }}</p>
          <p>價目版本：v{{ catalog.version }}</p>
        </div>
      </header>

      <div class="quote-sheet__parties">
        <div class="quote-sheet__customer-grid">
          <div class="quote-sheet__customer-col">
            <p><strong>客戶抬頭：{{ form.customerName || '（請填寫客戶名稱）' }}</strong></p>
            <p v-if="form.taxId">統一編號：{{ form.taxId }}</p>
            <p v-if="form.address">地址：{{ form.address }}</p>
          </div>
          <div class="quote-sheet__customer-col">
            <p v-if="form.contactName">聯絡人：{{ form.contactName }}</p>
            <p v-if="form.contactPhone">電話：{{ form.contactPhone }}</p>
            <p v-if="form.contactEmail">Email：{{ form.contactEmail }}</p>
          </div>
        </div>
        <p class="quote-sheet__salesperson">
          業務窗口：{{ form.salespersonName || '—' }}
        </p>
      </div>

      <div class="quote-table-frame">
      <table class="quote-table">
        <colgroup>
          <col class="col-kind" />
          <col class="col-desc" />
          <col class="col-qty" />
          <col class="col-money" />
          <col class="col-money" />
        </colgroup>
        <thead>
          <tr>
            <th class="col-kind">項目</th>
            <th>說明</th>
            <th class="num col-qty">數量</th>
            <th class="num col-money">表價（未稅）</th>
            <th class="num col-money">報價（未稅）</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in quote.subscriptionItems" :key="item.code">
            <td class="col-kind">訂閱／月</td>
            <td>
              {{ item.name }}
              <span v-if="item.listAmount != null && item.quotedAmount != null && item.listAmount !== item.quotedAmount" class="deviated-mark">＊</span>
            </td>
            <td class="num col-qty">{{ formatQuantity(item) }}</td>
            <td class="num col-money">{{ formatAmountOrDash(item.listAmount) }}</td>
            <td class="num col-money" :class="{ 'quote-amount-cell': !formLocked && (isEnterprisePlanItem(item) || item.negotiated || item.adjustable) }">
              <template v-if="!formLocked && isEnterprisePlanItem(item)">
                <input
                  type="number"
                  min="0"
                  class="quote-inline-input no-print"
                  :placeholder="enterpriseQuotePlaceholder(item)"
                  :value="enterpriseOverrideInputValue(item)"
                  @input="setEnterpriseOverride(item.code, $event.target.value)"
                />
                <span class="print-only">{{ formatAmountOrDash(item.quotedAmount) }}</span>
              </template>
              <template v-else-if="!formLocked && item.negotiated">
                <input
                  v-model="form.overridePlanMonthly"
                  type="number"
                  min="0"
                  class="quote-inline-input no-print"
                  placeholder="請填月費"
                />
                <span class="print-only">{{ formatAmountOrDash(item.quotedAmount) }}</span>
              </template>
              <template v-else-if="!formLocked && item.adjustable">
                <input
                  type="number"
                  min="0"
                  class="quote-inline-input no-print"
                  :placeholder="item.listAmount != null ? String(item.listAmount) : ''"
                  :value="moduleOverrideInputValue(item)"
                  @input="setModulePriceOverride(item.code, $event.target.value)"
                />
                <span class="print-only">{{ formatAmountOrDash(item.quotedAmount) }}</span>
              </template>
              <template v-else>
                {{ formatAmountOrDash(item.quotedAmount) }}
              </template>
            </td>
          </tr>
          <tr class="row-sub">
            <td colspan="4">{{ quote.periodLabel }}</td>
            <td class="num col-money">{{ formatMoney(quote.billingPeriodAmount) }}</td>
          </tr>
          <tr v-for="item in serviceFeeItems" :key="item.code">
            <td class="col-kind">{{ serviceFeeKindLabel(item) }}</td>
            <td>
              {{ item.name }}
              <span v-if="item.listAmount != null && item.quotedAmount != null && item.listAmount !== item.quotedAmount" class="deviated-mark">＊</span>
            </td>
            <td class="num col-qty">{{ formatQuantity(item) }}</td>
            <td class="num col-money">{{ formatAmountOrDash(item.listAmount) }}</td>
            <td class="num col-money" :class="{ 'quote-amount-cell': !formLocked }">
              <input
                v-if="!formLocked"
                type="number"
                min="0"
                class="quote-inline-input no-print"
                :placeholder="item.listAmount != null ? String(item.listAmount) : ''"
                :value="oneTimeOverrideInputValue(item)"
                @input="setOneTimeOverride(item.code, $event.target.value)"
              />
              <span :class="{ 'print-only': !formLocked }">{{ formatAmountOrDash(item.quotedAmount) }}</span>
            </td>
          </tr>
          <tr v-if="serviceFeeItems.length" class="row-sub">
            <td colspan="4">單次／加購小計</td>
            <td class="num col-money">{{ formatMoney(serviceFeeTotal) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="first-year-summary">
            <td colspan="4">首年未稅小計</td>
            <td class="num">{{ formatMoney(quote.subtotalExclTax) }}</td>
          </tr>
          <tr class="first-year-summary">
            <td colspan="4">首年營業稅 {{ Math.round((catalog.taxRate || 0) * 100) }}%</td>
            <td class="num">{{ formatMoney(quote.taxAmount) }}</td>
          </tr>
          <tr class="first-year-summary total-row grand-total-row">
            <td colspan="4">首年含稅總計</td>
            <td class="num col-money">{{ formatMoney(quote.totalInclTax) }}</td>
          </tr>
        </tfoot>
      </table>
      </div>

      <p v-if="quote.isDeviated" class="deviated-note">
        ＊ 標示項目已偏離表價，為業務議價結果；表價另見 LOCKLINE+ 產品定價表（經銷商版）。
      </p>

      <div v-if="form.notes" class="quote-notes">
        <h3>備註</h3>
        <p>{{ form.notes }}</p>
      </div>

      <div class="quote-terms">
        <h3>條款說明</h3>
        <ol>
          <li v-for="(term, idx) in catalog.terms" :key="idx">{{ term }}</li>
        </ol>
      </div>

      <p class="quote-foot">
        計價數量（估價用，有效好友或會員數取高者）：{{ Number(form.effectiveFriends || 0).toLocaleString('zh-TW') }}
        ｜ 方案：{{ selectedPlan?.name || '—' }}
      </p>
    </section>

    <section v-else v-show="activeTab === 'estimate' && estimateSubTab === 'preview'" class="quote-sheet" aria-label="經銷價估價單">
      <header class="quote-sheet__header">
        <div>
          <p class="quote-sheet__brand">LOCKLINE+</p>
          <h2 class="quote-sheet__title">經銷價估價單</h2>
        </div>
        <div class="quote-sheet__meta">
          <p v-if="savedQuote">{{ savedQuote.quoteNo }}　第 {{ savedQuote.viewingVersion }} 版</p>
          <p>報價日期：{{ form.quoteDate || '—' }}</p>
          <p>有效期限：{{ form.validUntil || '—' }}</p>
          <p>價目版本：v{{ catalog.version }}</p>
        </div>
      </header>

      <div class="quote-sheet__parties">
        <p><strong>客戶抬頭：{{ form.customerName || '（請填寫客戶名稱）' }}</strong></p>
        <p class="quote-sheet__salesperson">{{ dealerShareResult.parameterSummary }}</p>
      </div>

      <div class="quote-table-frame">
        <table class="quote-table">
          <colgroup>
            <col class="col-kind" />
            <col class="col-desc" />
            <col class="col-money" />
            <col class="col-rate" />
            <col class="col-money" />
          </colgroup>
          <thead>
            <tr>
              <th class="col-kind">項目</th>
              <th>說明</th>
              <th class="num col-money">售價（未稅）{{ sellingPeriodSuffix }}</th>
              <th class="num col-rate">折扣</th>
              <th class="num col-money">經銷價（未稅）</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in dealerShareResult.subscriptionLines" :key="`share-${item.code}`">
              <td class="col-kind">{{ item.kindLabel }}</td>
              <td>
                {{ item.name }}
                <span v-if="item.note" class="renewal-ref-note">{{ item.note }}</span>
              </td>
              <td class="num col-money">{{ formatMoneyOrDash(item.basisAmount) }}</td>
              <td class="num col-rate quote-amount-cell">
                <input
                  v-if="!formLocked"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  class="quote-inline-input share-rate-input no-print"
                  :value="payableRateInputValue(item)"
                  @input="setPayableRateOverride(item.code, $event.target.value)"
                />
                <button
                  v-if="!formLocked && item.overridden"
                  type="button"
                  class="share-rate-reset no-print"
                  @click="clearShareRateOverride(item.code)"
                >
                  恢復
                </button>
                <span :class="{ 'print-only': !formLocked }">{{ formatShareRate(item.payableRate) }}</span>
              </td>
              <td class="num col-money">{{ formatMoneyOrDash(item.payableAmount) }}</td>
            </tr>
            <tr v-if="dealerShareResult.subscriptionLines.length" class="row-sub">
              <td colspan="2">本約訂閱</td>
              <td class="num col-money">{{ formatMoney(dealerShareResult.subscriptionBasis) }}</td>
              <td></td>
              <td class="num col-money">{{ formatMoney(dealerShareResult.subscriptionPayable) }}</td>
            </tr>
            <tr v-for="item in dealerShareResult.serviceLines" :key="`share-${item.code}`">
              <td class="col-kind">{{ item.kindLabel }}</td>
              <td>
                {{ item.name }}
                <span v-if="item.note" class="renewal-ref-note">{{ item.note }}</span>
              </td>
              <td class="num col-money">{{ formatMoneyOrDash(item.basisAmount) }}</td>
              <td class="num col-rate quote-amount-cell">
                <input
                  v-if="!formLocked"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  class="quote-inline-input share-rate-input no-print"
                  :value="payableRateInputValue(item)"
                  @input="setPayableRateOverride(item.code, $event.target.value)"
                />
                <button
                  v-if="!formLocked && item.overridden"
                  type="button"
                  class="share-rate-reset no-print"
                  @click="clearShareRateOverride(item.code)"
                >
                  恢復
                </button>
                <span :class="{ 'print-only': !formLocked }">{{ formatShareRate(item.payableRate) }}</span>
              </td>
              <td class="num col-money">{{ formatMoneyOrDash(item.payableAmount) }}</td>
            </tr>
            <tr v-if="dealerShareResult.serviceLines.length" class="row-sub">
              <td colspan="2">單次／年費</td>
              <td class="num col-money">{{ formatMoney(dealerShareResult.serviceBasis) }}</td>
              <td></td>
              <td class="num col-money">{{ formatMoney(dealerShareResult.servicePayable) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="first-year-summary total-row">
              <td colspan="2">首年合計（未稅）</td>
              <td class="num col-money">{{ formatMoney(dealerShareResult.firstYearBasis) }}</td>
              <td></td>
              <td class="num col-money">{{ formatMoney(dealerShareResult.firstYearPayable) }}</td>
            </tr>
            <tr class="first-year-summary total-row grand-total-row">
              <td colspan="2">首年合計（含稅）</td>
              <td class="num col-money">{{ formatMoney(amountInclTax(dealerShareResult.firstYearBasis)) }}</td>
              <td></td>
              <td class="num col-money">{{ formatMoney(amountInclTax(dealerShareResult.firstYearPayable)) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="quote-terms">
        <h3>經銷價說明</h3>
        <ol>
          <li v-for="(note, idx) in dealerShareResult.footnotes" :key="`share-note-${idx}`">{{ note }}</li>
        </ol>
      </div>

      <div v-if="form.notes" class="quote-notes">
        <h3>備註</h3>
        <p>{{ form.notes }}</p>
      </div>

      <p class="quote-foot">
        計價數量：{{ Number(form.effectiveFriends || 0).toLocaleString('zh-TW') }}
        ｜ 方案：{{ selectedPlan?.name || '—' }}
      </p>
    </section>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import catalog from '@/data/dealerPricingCatalog.json';
import { calculateQuote, SERVICE_FEE_ADDON_CODES } from '@/utils/quoteCalculator';
import {
  calculateDealerShare,
  defaultDealerShareParams,
  normalizeDealerShareParams
} from '@/utils/dealerShare';
import { mergeQuotePlans } from '@/utils/quotePlanCatalog';
import { planService } from '@/api/modules/plans';
import { quoteEstimateService } from '@/api/modules/quoteEstimates';
import { chooseInvoiceParty, showConfirm, showError, showSuccess } from '@/utils/swal';
import authService from '@/helpers/authservice/authService';

/** 互斥選購：勾其一不可勾另一 */
const EXCLUSIVE_MODULE_PAIRS = [
  ['content_publishing', 'marketing_ops']
];

function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addDaysISO(baseISO, days) {
  const d = baseISO ? new Date(`${baseISO}T00:00:00`) : new Date();
  d.setDate(d.getDate() + days);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 企業版 30 天，其餘方案 7 天 */
function resolveQuoteValidDays(planCode, pricingCatalog = catalog) {
  const byPlan = pricingCatalog.quoteValidDaysByPlan || {};
  if (planCode && byPlan[planCode] != null) return Number(byPlan[planCode]);
  return Number(pricingCatalog.quoteValidDaysDefault) || 7;
}

function defaultForm() {
  const user = authService.getCurrentUser() || {};
  const quoteDate = todayISO();
  const planCode = 'enterprise';
  return {
    customerName: '',
    taxId: '',
    address: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    salespersonName: user.name || user.username || user.displayName || '',
    quoteDate,
    validUntil: addDaysISO(quoteDate, resolveQuoteValidDays(planCode)),
    planCode,
    setupChoice: 'standard',
    effectiveFriends: 0,
    billingCycle: 'yearly',
    moduleCodes: [],
    annualAddonCodes: [],
    chatdeskExtraSeats: 0,
    extraLineOa: 0,
    extraRichMenus: 0,
    extraTrainingSessions: 0,
    pointsImportEnabled: false,
    pointsRecords: 0,
    pointsExtraSources: 0,
    pointsNeedsCleaning: false,
    pointsCleaningFee: catalog.pointsImport.cleaningFeeFrom,
    smsPointsEnabled: false,
    smsPointsQty: 1000,
    aiCreditTopupEnabled: false,
    aiCreditTopupAmount: catalog.aiCreditTopup?.minAmount ?? 1000,
    overrideEnterpriseBase: '',
    overrideEnterpriseFriends: '',
    overridePlanMonthly: '',
    oneTimeOverrides: {},
    modulePriceOverrides: {},
    customItems: [],
    notes: '',
    quoteAudience: 'customer',
    dealerShare: defaultDealerShareParams()
  };
}

export default {
  name: 'SystemQuoteEstimator',
  data() {
    return {
      catalog: { ...catalog, plans: [...catalog.plans] },
      plansLoading: false,
      plansError: '',
      activeTab: 'saved',
      estimateSubTab: 'share',
      sheetView: 'dealer',
      form: defaultForm(),
      docxExporting: false,
      quoteSaving: false,
      applyingSavedForm: false,
      savedQuote: null,
      savedQuotes: [],
      savedLoading: false,
      savedError: '',
      savedSearch: '',
      savedPage: 1,
      savedTotalPages: 1,
      savedSearchTimer: null,
      nonAlphanumericIme: {
        taxId: false,
        contactPhone: false
      }
    };
  },
  computed: {
    quoteVersions() {
      const versions = this.savedQuote?.versions || [];
      return [...versions].sort((a, b) => Number(a.versionNo) - Number(b.versionNo));
    },
    isViewingLatest() {
      if (!this.savedQuote) return true;
      return Number(this.savedQuote.viewingVersion) === Number(this.savedQuote.currentVersion);
    },
    quoteWon() {
      return this.savedQuote?.status === 'won';
    },
    dealVersionNo() {
      if (!this.savedQuote) return null;
      return Number(this.savedQuote.wonVersion) || Number(this.savedQuote.currentVersion);
    },
    viewingHistoricalQuote() {
      return this.quoteWon && Number(this.savedQuote.viewingVersion) !== this.dealVersionNo;
    },
    formLocked() {
      return Boolean(this.savedQuote) && (!this.isViewingLatest || this.quoteWon);
    },
    enterprisePlan() {
      return this.catalog.plans.find((p) => p.code === 'enterprise') || null;
    },
    enterpriseBaseFeeLabel() {
      return this.enterprisePlan?.baseFeeLabel
        || '企業版基本月費（會員／門市／員工帳號無上限；含 API、POS；LINE OA 1 個）';
    },
    quoteValidDays() {
      return resolveQuoteValidDays(this.form.planCode, this.catalog);
    },
    enterpriseTierRows() {
      const plan = this.enterprisePlan;
      const tiers = plan?.tiers || [];
      const baseMonthly = tiers[0]?.monthlyPrice ?? 10000;
      return tiers.map((tier, index) => {
        const prevMax = index === 0 ? null : tiers[index - 1].maxFriends;
        const rangeLabel = prevMax == null
          ? `≤${this.formatFriends(tier.maxFriends)}`
          : `${this.formatFriends(prevMax + 1)}–${this.formatFriends(tier.maxFriends)}`;
        return {
          maxFriends: tier.maxFriends,
          rangeLabel,
          baseMonthly,
          friendsSurcharge: Math.max(0, tier.monthlyPrice - baseMonthly),
          monthlyPrice: tier.monthlyPrice,
          yearlyPrice: tier.monthlyPrice * 10
        };
      });
    },
    selectedPlan() {
      return this.catalog.plans.find((p) => p.code === this.form.planCode) || null;
    },
    chatdeskModule() {
      return this.catalog.modules.find((m) => m.code === 'chatdesk') || null;
    },
    serviceFeeItems() {
      // 試算已排好：導入等在前，簡訊／AI／DDPay／星益欣連在一起
      return this.quote.serviceFeeItems || [];
    },
    serviceFeeTotal() {
      return (Number(this.quote.oneTimeTotal) || 0) + (Number(this.quote.annualTotal) || 0);
    },
    quoteInput() {
      const overrides = {};
      if (this.form.overrideEnterpriseBase !== '' && this.form.overrideEnterpriseBase != null) {
        overrides.enterpriseBase = Number(this.form.overrideEnterpriseBase);
      }
      if (this.form.overrideEnterpriseFriends !== '' && this.form.overrideEnterpriseFriends != null) {
        overrides.enterpriseFriendsSurcharge = Number(this.form.overrideEnterpriseFriends);
      }
      if (
        this.selectedPlan
        && this.selectedPlan.code !== 'enterprise'
        && this.selectedPlan.monthlyPrice == null
        && this.form.overridePlanMonthly !== ''
        && this.form.overridePlanMonthly != null
      ) {
        overrides.monthlySubscription = Number(this.form.overridePlanMonthly);
      }
      if (this.form.modulePriceOverrides && Object.keys(this.form.modulePriceOverrides).length) {
        overrides.moduleAmounts = { ...this.form.modulePriceOverrides };
      }
      if (this.form.oneTimeOverrides && Object.keys(this.form.oneTimeOverrides).length) {
        overrides.oneTimeAmounts = { ...this.form.oneTimeOverrides };
        if (
          this.form.oneTimeOverrides.setup_fee !== undefined
          && this.form.oneTimeOverrides.setup_fee !== ''
        ) {
          overrides.setupFee = Number(this.form.oneTimeOverrides.setup_fee);
        }
      }

      return {
        planCode: this.form.planCode,
        effectiveFriends: this.form.effectiveFriends,
        billingCycle: this.form.billingCycle,
        moduleCodes: this.form.moduleCodes,
        annualAddonCodes: this.form.annualAddonCodes,
        chatdeskExtraSeats: this.form.chatdeskExtraSeats,
        enterpriseAddons: {
          extraLineOa: this.form.extraLineOa,
          extraRichMenus: this.form.extraRichMenus,
          extraTrainingSessions: this.form.extraTrainingSessions
        },
        pointsImport: {
          enabled: this.form.pointsImportEnabled,
          records: this.form.pointsRecords,
          needsCleaning: this.form.pointsNeedsCleaning,
          cleaningFee: this.form.pointsCleaningFee,
          extraSources: this.form.pointsExtraSources
        },
        smsPoints: {
          enabled: this.form.smsPointsEnabled,
          points: this.form.smsPointsQty
        },
        aiCreditTopup: {
          enabled: this.form.aiCreditTopupEnabled,
          amount: this.form.aiCreditTopupAmount
        },
        premiumSetup: this.form.setupChoice === 'premium',
        customItems: this.form.customItems,
        overrides,
        catalog: this.catalog
      };
    },
    quote() {
      return calculateQuote(this.quoteInput);
    },
    isDealerSheet() {
      return this.sheetView === 'dealer';
    },
    isReferralDeal() {
      return this.form.dealerShare?.enterpriseDeal === 'referral';
    },
    sellingPeriodSuffix() {
      const code = this.form.billingCycle;
      if (code === 'yearly') return '/年';
      if (code === 'quarterly') return '/季';
      if (code === 'semiannual') return '/半年';
      return '/月';
    },
    dealerShareResult() {
      return calculateDealerShare(this.quote, this.form.dealerShare, this.catalog);
    },
    emailError() {
      const value = String(this.form.contactEmail || '').trim();
      if (!value) return '';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return '請輸入正確的 Email 格式';
      return '';
    },
    canPrint() {
      return Boolean(
        this.form.customerName
        && this.form.customerName.trim()
        && this.quote.canExport
        && !this.emailError
      );
    },
    canSave() {
      if (this.formLocked) return false;
      return Boolean(this.form.customerName && this.form.customerName.trim() && !this.emailError);
    }
  },
  mounted() {
    this.loadQuotePlans();
    this.fetchSavedQuotes();
  },
  watch: {
    'form.quoteDate'(val, oldVal) {
      if (this.applyingSavedForm || this.formLocked || !val) return;
      const days = this.quoteValidDays;
      const previousDefault = oldVal ? addDaysISO(oldVal, days) : null;
      // 僅在有效期限仍為「舊報價日 + 預設天數」時自動跟調，避免覆寫手動修改
      if (!this.form.validUntil || this.form.validUntil === previousDefault) {
        this.form.validUntil = addDaysISO(val, days);
      }
    },
    'form.planCode'(val, oldVal) {
      if (this.formLocked) return;
      if (!this.applyingSavedForm && val !== oldVal) {
        this.form.overridePlanMonthly = '';
        if (this.form.oneTimeOverrides?.setup_fee != null) {
          const next = { ...this.form.oneTimeOverrides };
          delete next.setup_fee;
          this.form.oneTimeOverrides = next;
        }
      }
      if (this.applyingSavedForm || !this.form.quoteDate) return;
      const prevDays = resolveQuoteValidDays(oldVal, this.catalog);
      const nextDays = resolveQuoteValidDays(val, this.catalog);
      const previousDefault = addDaysISO(this.form.quoteDate, prevDays);
      // 有效期限仍為前一方案預設時，改為新方案預設天數
      if (!this.form.validUntil || this.form.validUntil === previousDefault) {
        this.form.validUntil = addDaysISO(this.form.quoteDate, nextDays);
      }
    }
  },
  methods: {
    resolveQuoteValidDays,
    focusAlphanumeric(event) {
      const el = event.target;
      if (!el) return;
      el.lang = 'en';
      el.style.imeMode = 'disabled';
      const active = document.activeElement;
      const typingElsewhere = active
        && active !== el
        && typeof active.matches === 'function'
        && active.matches('input, textarea, select, [contenteditable="true"]');
      if (!typingElsewhere && active !== el) {
        el.focus();
      }
    },
    markNonAlphanumericIme(field) {
      this.nonAlphanumericIme[field] = true;
    },
    clearNonAlphanumericIme(field) {
      this.nonAlphanumericIme[field] = false;
    },
    onDigitsInput(field, event, maxLength) {
      const raw = String(event.target.value || '');
      const composing = event.isComposing || /[\u3000-\u303f\u3040-\u30ff\u3400-\u9fff\uff00-\uffef]/.test(raw);
      if (composing) {
        this.nonAlphanumericIme[field] = true;
      } else if (/^\d*$/.test(raw)) {
        this.nonAlphanumericIme[field] = false;
      }
      const digits = raw.replace(/\D/g, '');
      const next = maxLength ? digits.slice(0, maxLength) : digits;
      this.form[field] = next;
      event.target.value = next;
    },
    formatFriends(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '—';
      return n.toLocaleString('zh-TW');
    },
    formatAmount(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '—';
      return n.toLocaleString('zh-TW');
    },
    formatAmountOrDash(value) {
      if (value == null || value === '') return '—';
      return this.formatAmount(value);
    },
    formatMoney(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '—';
      return `NT$ ${n.toLocaleString('zh-TW')}`;
    },
    amountInclTax(value) {
      const base = Number(value);
      if (!Number.isFinite(base)) return null;
      const tax = Math.round(base * (Number(this.catalog.taxRate) || 0) * 100) / 100;
      return Math.round((base + tax) * 100) / 100;
    },
    formatMoneyOrDash(value) {
      if (value == null || value === '') return '—';
      return this.formatMoney(value);
    },
    formatQuantity(item) {
      const qty = item?.quantity;
      if (qty == null || qty === '') return '—';
      const n = Number(qty);
      if (!Number.isFinite(n)) return '—';
      const label = item.unitLabel ? String(item.unitLabel) : '';
      return `${n.toLocaleString('zh-TW')}${label}`;
    },
    enterpriseAddonPrice(code) {
      const addon = (this.catalog.enterpriseAddons || []).find((item) => item.code === code);
      return addon?.unitPrice ?? 0;
    },
    setupFeeTitle(plan) {
      if (!plan) return '建置費';
      return plan.setupFeeLabel || `${plan.name}導入費`;
    },
    planOptionLabel(plan) {
      if (!plan) return '';
      if (plan.code === 'enterprise') {
        return `${plan.name}（月費依好友級距，${this.setupFeeTitle(plan)} ${this.formatMoney(plan.setupFee)}）`;
      }
      const monthly = plan.monthlyPrice == null
        ? '月費另議'
        : `月費 ${this.formatMoney(plan.monthlyPrice)}`;
      const setup = plan.setupFee
        ? `，${this.setupFeeTitle(plan)} ${this.formatMoney(plan.setupFee)}`
        : '';
      return `${plan.name}（${monthly}${setup}）`;
    },
    async loadQuotePlans() {
      this.plansLoading = true;
      this.plansError = '';
      try {
        const items = await this.fetchActivePlans();
        const plans = mergeQuotePlans(catalog, items);
        if (!plans.length) {
          this.plansError = '資料庫沒有可報價的方案，暫用價目表方案';
          return;
        }
        this.catalog = { ...this.catalog, plans };
        if (!plans.some((plan) => plan.code === this.form.planCode)) {
          const fallback = plans.find((plan) => plan.code === 'enterprise') || plans[0];
          this.form.planCode = fallback.code;
        }
      } catch (err) {
        this.plansError = err?.message || '無法讀取方案，暫用價目表方案';
      } finally {
        this.plansLoading = false;
      }
    },
    async fetchActivePlans() {
      const pageSize = 100;
      const items = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await planService.getPlans({
          status: 'active',
          page,
          pageSize
        });
        const data = response?.data || {};
        const batch = Array.isArray(data.items) ? data.items : [];
        items.push(...batch);
        totalPages = Number(data.pagination?.totalPages) || 1;
        page += 1;
      } while (page <= totalPages);
      return items;
    },
    resetForm() {
      this.applyingSavedForm = true;
      this.form = defaultForm();
      this.savedQuote = null;
      this.estimateSubTab = 'share';
      this.sheetView = 'dealer';
      this.activeTab = 'estimate';
      this.$nextTick(() => {
        this.applyingSavedForm = false;
      });
    },
    buildSavePayload() {
      return {
        form: JSON.parse(JSON.stringify(this.form)),
        summary: {
          planCode: this.form.planCode,
          planName: this.selectedPlan?.name || '',
          totalInclTax: this.quote.totalInclTax,
          catalogVersion: this.catalog.version,
          effectiveFriends: this.form.effectiveFriends,
          billingCycle: this.form.billingCycle
        }
      };
    },
    applySavedRecord(data, options = {}) {
      const formData = data?.formData || {};
      const base = defaultForm();
      this.applyingSavedForm = true;
      this.form = {
        ...base,
        ...formData,
        moduleCodes: Array.isArray(formData.moduleCodes) ? [...formData.moduleCodes] : [],
        annualAddonCodes: Array.isArray(formData.annualAddonCodes) ? [...formData.annualAddonCodes] : [],
        customItems: Array.isArray(formData.customItems) ? formData.customItems.map((item) => ({ ...item })) : [],
        oneTimeOverrides: formData.oneTimeOverrides ? { ...formData.oneTimeOverrides } : {},
        modulePriceOverrides: formData.modulePriceOverrides ? { ...formData.modulePriceOverrides } : {},
        quoteAudience: formData.quoteAudience === 'dealer' ? 'dealer' : 'customer',
        dealerShare: normalizeDealerShareParams(formData.dealerShare)
      };
      const versions = [...(data.versions || [])].sort(
        (a, b) => Number(a.versionNo) - Number(b.versionNo)
      );
      this.savedQuote = {
        id: data.id,
        quoteNo: data.quoteNo,
        currentVersion: data.currentVersion,
        viewingVersion: data.versionNo,
        status: data.status === 'won' ? 'won' : 'open',
        invoiceParty: data.invoiceParty === 'dealer' || data.invoiceParty === 'customer'
          ? data.invoiceParty
          : null,
        wonVersion: Number(data.wonVersion) || (data.status === 'won' ? Number(data.currentVersion) : null),
        versions
      };
      this.activeTab = 'estimate';
      if (this.savedQuote.status === 'won') {
        this.sheetView = this.savedQuote.invoiceParty === 'dealer' ? 'dealer' : 'customer';
      }
      if (!options.keepSubTab) {
        this.estimateSubTab = 'customer';
        if (this.savedQuote.status !== 'won') this.sheetView = 'customer';
      }
      this.$nextTick(() => {
        this.applyingSavedForm = false;
      });
    },
    async saveQuote() {
      if (!this.canSave || this.quoteSaving) return;
      this.quoteSaving = true;
      try {
        const response = await quoteEstimateService.create(this.buildSavePayload());
        const created = response?.data || {};
        await this.openSavedQuote(created.id, created.currentVersion || 1);
        await showSuccess('已存檔', `${created.quoteNo || ''} 第 1 版`);
      } catch (err) {
        await showError('存檔失敗', err?.response?.data?.message || err?.message || '請稍後再試');
      } finally {
        this.quoteSaving = false;
      }
    },
    async markQuoteWon() {
      if (!this.savedQuote || this.quoteWon || this.quoteSaving) return;
      const choice = await chooseInvoiceParty({
        versions: this.quoteVersions,
        defaultVersion: this.savedQuote.currentVersion
      });
      if (!choice) return;
      this.quoteSaving = true;
      try {
        await quoteEstimateService.markWon(this.savedQuote.id, choice);
        await this.openSavedQuote(this.savedQuote.id, choice.versionNo, { keepSubTab: true, force: true });
        await showSuccess('已成交', `已以第 ${choice.versionNo} 版成交，記錄為${this.invoicePartyLabel(choice.invoiceParty)}，之後發票依此開立`);
      } catch (err) {
        await showError('設定失敗', err?.response?.data?.message || err?.message || '請稍後再試');
      } finally {
        this.quoteSaving = false;
      }
    },
    async deleteSavedQuote(id) {
      if (!id || this.quoteSaving) return;
      const confirmed = await showConfirm(
        '刪除報價',
        '未成交報價會連同所有版本刪除，且無法復原。',
        '刪除',
        '取消'
      );
      if (!confirmed) return;
      this.quoteSaving = true;
      try {
        await quoteEstimateService.remove(id);
        if (this.savedQuote?.id === id) {
          this.applyingSavedForm = true;
          this.form = defaultForm();
          this.savedQuote = null;
          this.activeTab = 'saved';
          this.$nextTick(() => {
            this.applyingSavedForm = false;
          });
        }
        if (this.activeTab === 'saved') {
          await this.fetchSavedQuotes();
        }
        await showSuccess('已刪除', '這張未成交報價已刪除');
      } catch (err) {
        await showError('刪除失敗', err?.response?.data?.message || err?.message || '請稍後再試');
      } finally {
        this.quoteSaving = false;
      }
    },
    async saveQuoteVersion() {
      if (!this.savedQuote || this.quoteWon || !this.isViewingLatest || !this.canSave || this.quoteSaving) return;
      this.quoteSaving = true;
      try {
        const response = await quoteEstimateService.createVersion(
          this.savedQuote.id,
          this.buildSavePayload()
        );
        const created = response?.data || {};
        const versionNo = created.currentVersion || created.version?.versionNo;
        await this.openSavedQuote(this.savedQuote.id, versionNo);
        await showSuccess('已另存版本', `${created.quoteNo || this.savedQuote.quoteNo} 第 ${versionNo} 版`);
      } catch (err) {
        await showError('另存失敗', err?.response?.data?.message || err?.message || '請稍後再試');
      } finally {
        this.quoteSaving = false;
      }
    },
    backToList() {
      this.activeTab = 'saved';
      this.fetchSavedQuotes();
    },
    onSavedSearch() {
      if (this.savedSearchTimer) clearTimeout(this.savedSearchTimer);
      this.savedSearchTimer = setTimeout(() => {
        this.savedPage = 1;
        this.fetchSavedQuotes();
      }, 300);
    },
    changeSavedPage(page) {
      this.savedPage = page;
      this.fetchSavedQuotes();
    },
    async fetchSavedQuotes() {
      this.savedLoading = true;
      this.savedError = '';
      try {
        const response = await quoteEstimateService.list({
          page: this.savedPage,
          pageSize: 20,
          search: this.savedSearch || undefined
        });
        const data = response?.data || {};
        this.savedQuotes = data.items || [];
        this.savedTotalPages = data.pagination?.totalPages || 1;
      } catch (err) {
        this.savedQuotes = [];
        this.savedError = err?.response?.data?.message || err?.message || '無法讀取已存報價';
      } finally {
        this.savedLoading = false;
      }
    },
    isViewingVersion(versionNo) {
      return Number(this.savedQuote?.viewingVersion) === Number(versionNo);
    },
    isViewingSavedVersion(item, versionNo) {
      return this.savedQuote?.id === item.id && this.isViewingVersion(versionNo);
    },
    itemVersions(item) {
      const versions = Array.isArray(item?.versions) ? item.versions : [];
      if (versions.length > 0) {
        return [...versions].sort((a, b) => Number(a.versionNo) - Number(b.versionNo));
      }
      const current = Number(item?.currentVersion) || 1;
      return Array.from({ length: current }, (_, index) => ({ versionNo: index + 1 }));
    },
    async openSavedQuote(id, version, options = {}) {
      const sameQuote = this.savedQuote && this.savedQuote.id === id;
      const sameVersion = version != null && Number(this.savedQuote?.viewingVersion) === Number(version);
      if (sameQuote && sameVersion && !options.force) {
        this.activeTab = 'estimate';
        return;
      }
      try {
        const response = await quoteEstimateService.get(id, version);
        this.applySavedRecord(response?.data || {}, options);
      } catch (err) {
        await showError('無法開啟', err?.response?.data?.message || err?.message || '請稍後再試');
      }
    },
    formatSavedAt(value) {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString('zh-TW', { hour12: false });
    },
    applyUpgradeSuggestion() {
      if (this.formLocked || !this.quote.upgradeSuggestion) return;
      this.form.planCode = this.quote.upgradeSuggestion.planCode;
      this.form.overrideEnterpriseBase = '';
      this.form.overrideEnterpriseFriends = '';
    },
    getExclusivePartner(moduleCode) {
      for (const pair of EXCLUSIVE_MODULE_PAIRS) {
        if (pair[0] === moduleCode) return pair[1];
        if (pair[1] === moduleCode) return pair[0];
      }
      return null;
    },
    isModuleDisabled(moduleCode) {
      const partner = this.getExclusivePartner(moduleCode);
      if (!partner) return false;
      return this.form.moduleCodes.includes(partner);
    },
    onModuleToggle(moduleCode, checked) {
      if (this.formLocked) return;
      let next = [...this.form.moduleCodes];
      if (checked) {
        if (!next.includes(moduleCode)) next.push(moduleCode);
        const partner = this.getExclusivePartner(moduleCode);
        if (partner) {
          next = next.filter((code) => code !== partner);
        }
      } else {
        next = next.filter((code) => code !== moduleCode);
      }
      this.form.moduleCodes = next;
    },
    onAnnualAddonToggle(code, checked) {
      if (this.formLocked) return;
      let next = [...this.form.annualAddonCodes];
      if (checked) {
        if (!next.includes(code)) next.push(code);
      } else {
        next = next.filter((c) => c !== code);
      }
      this.form.annualAddonCodes = next;
    },
    serviceFeeKindLabel(item) {
      if (String(item?.code || '').startsWith('custom_')) return '客製';
      if (SERVICE_FEE_ADDON_CODES.includes(item?.code)) return '加購';
      return '單次';
    },
    addCustomItem() {
      if (this.formLocked) return;
      this.form.customItems.push({
        id: `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
        name: '',
        description: '',
        quantity: 1,
        unitPrice: 0
      });
    },
    removeCustomItem(id) {
      if (this.formLocked) return;
      const code = `custom_${id}`;
      this.form.customItems = this.form.customItems.filter((item) => item.id !== id);
      if (this.form.oneTimeOverrides?.[code] != null) {
        const next = { ...this.form.oneTimeOverrides };
        delete next[code];
        this.form.oneTimeOverrides = next;
      }
    },
    customItemAmount(item) {
      const qty = Math.max(1, Math.floor(Number(item?.quantity) || 1));
      const unit = Math.max(0, Number(item?.unitPrice) || 0);
      return qty * unit;
    },
    isEnterprisePlanItem(item) {
      return item?.code === 'plan_enterprise_base' || item?.code === 'plan_enterprise_friends';
    },
    enterpriseQuotePlaceholder(item) {
      if (item?.listAmount != null) return String(item.listAmount);
      if (item?.code === 'plan_enterprise_friends') return '專案自填';
      return '';
    },
    enterpriseOverrideInputValue(item) {
      if (item?.code === 'plan_enterprise_base') {
        const raw = this.form.overrideEnterpriseBase;
        if (raw !== undefined && raw !== null && raw !== '') return raw;
      } else if (item?.code === 'plan_enterprise_friends') {
        const raw = this.form.overrideEnterpriseFriends;
        if (raw !== undefined && raw !== null && raw !== '') return raw;
      }
      return item?.quotedAmount != null && item.quotedAmount !== '' ? item.quotedAmount : '';
    },
    setEnterpriseOverride(code, value) {
      if (this.formLocked) return;
      const normalized = value === '' || value == null ? '' : value;
      if (code === 'plan_enterprise_base') {
        this.form.overrideEnterpriseBase = normalized;
        return;
      }
      if (code === 'plan_enterprise_friends') {
        this.form.overrideEnterpriseFriends = normalized;
      }
    },
    moduleOverrideInputValue(item) {
      const code = String(item?.code || '').replace(/^module_/, '');
      const raw = this.form.modulePriceOverrides?.[code];
      if (raw !== undefined && raw !== null && raw !== '') return raw;
      return item?.quotedAmount != null ? item.quotedAmount : '';
    },
    setModulePriceOverride(itemCode, value) {
      if (this.formLocked) return;
      const code = String(itemCode || '').replace(/^module_/, '');
      const next = { ...(this.form.modulePriceOverrides || {}) };
      if (value === '' || value == null) delete next[code];
      else next[code] = value;
      this.form.modulePriceOverrides = next;
    },
    oneTimeOverrideInputValue(item) {
      const raw = this.form.oneTimeOverrides?.[item.code];
      if (raw !== undefined && raw !== null && raw !== '') return raw;
      return item.quotedAmount != null ? item.quotedAmount : '';
    },
    setOneTimeOverride(code, value) {
      if (this.formLocked) return;
      const next = { ...(this.form.oneTimeOverrides || {}) };
      if (value === '' || value == null) {
        delete next[code];
      } else {
        next[code] = value;
      }
      this.form.oneTimeOverrides = next;
    },
    quotePdfBaseName() {
      const name = String(this.form.customerName || '')
        .trim()
        .replace(/[\\/:*?"<>|]/g, '');
      const rawDate = this.form.quoteDate || todayISO();
      const ymd = String(rawDate).replace(/\D/g, '').slice(0, 8);
      const suffix = this.isDealerSheet ? '-經銷價' : '';
      return `LOCKLINE+${name}-${ymd}${suffix}`;
    },
    setSheetView(view) {
      if (this.quoteWon) return;
      this.sheetView = view === 'dealer' ? 'dealer' : 'customer';
    },
    invoicePartyLabel(party) {
      if (party === 'dealer') return '對經銷商報價';
      if (party === 'customer') return '對客戶報價';
      return '未記錄報價對象';
    },
    setEnterpriseDeal(value) {
      if (this.formLocked) return;
      const deal = value === 'referral' ? 'referral' : 'dealer';
      const next = {
        ...this.form.dealerShare,
        enterpriseDeal: deal
      };
      if (deal === 'referral' && (next.referralRate == null || next.referralRate === '')) {
        next.referralRate = 20;
      }
      this.form.dealerShare = next;
    },
    setDealerTier(tier) {
      if (this.formLocked || this.isReferralDeal) return;
      const next = { ...this.form.dealerShare, tier };
      if (tier === 'standard') next.subscriptionRate = 30;
      if (tier === 'advanced') next.subscriptionRate = 40;
      this.form.dealerShare = next;
    },
    setSubscriptionRate(value) {
      if (this.formLocked || this.isReferralDeal) return;
      const rate = value === '' || value == null ? 0 : Number(value);
      let tier = 'custom';
      if (rate === 30) tier = 'standard';
      if (rate === 40) tier = 'advanced';
      this.form.dealerShare = {
        ...this.form.dealerShare,
        tier,
        subscriptionRate: rate
      };
    },
    payableRateInputValue(item) {
      const share = Number(this.shareRateInputValue(item));
      if (!Number.isFinite(share)) return '';
      return Math.round((100 - share) * 10) / 10;
    },
    setPayableRateOverride(code, value) {
      if (value === '' || value == null) {
        this.setShareRateOverride(code, '');
        return;
      }
      const payable = Number(value);
      if (!Number.isFinite(payable)) return;
      this.setShareRateOverride(code, String(Math.round((100 - payable) * 10) / 10));
    },
    shareRateInputValue(item) {
      const raw = this.form.dealerShare?.rateOverrides?.[item.code];
      if (raw != null && raw !== '') return raw;
      return item.defaultRate;
    },
    setShareRateOverride(code, value) {
      if (this.formLocked) return;
      const next = { ...(this.form.dealerShare.rateOverrides || {}) };
      if (value === '' || value == null) delete next[code];
      else next[code] = value;
      this.form.dealerShare = {
        ...this.form.dealerShare,
        rateOverrides: next
      };
    },
    clearShareRateOverride(code) {
      this.setShareRateOverride(code, '');
    },
    formatShareRate(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '—';
      const text = Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);
      return `${text}%`;
    },
    handlePrint() {
      if (!this.canPrint) return;
      const previousTitle = document.title;
      document.title = this.quotePdfBaseName();
      const restore = () => {
        document.title = previousTitle;
        window.removeEventListener('afterprint', restore);
      };
      window.addEventListener('afterprint', restore);
      window.print();
    },
    quoteLineName(item) {
      const name = item?.name || '';
      const deviated = item?.listAmount != null
        && item?.quotedAmount != null
        && item.listAmount !== item.quotedAmount;
      return deviated ? `${name}＊` : name;
    },
    quoteDocxModel() {
      const taxPercent = Math.round((this.catalog.taxRate || 0) * 100);
      return {
        quoteNo: this.savedQuote?.quoteNo || '',
        versionNo: this.savedQuote?.viewingVersion || null,
        customerName: this.form.customerName,
        taxId: this.form.taxId,
        address: this.form.address,
        contactName: this.form.contactName,
        contactPhone: this.form.contactPhone,
        contactEmail: this.form.contactEmail,
        salespersonName: this.form.salespersonName,
        quoteDate: this.form.quoteDate,
        validUntil: this.form.validUntil,
        catalogVersion: this.catalog.version,
        subscriptionItems: (this.quote.subscriptionItems || []).map((item) => ({
          name: this.quoteLineName(item),
          qty: this.formatQuantity(item),
          listAmount: this.formatAmountOrDash(item.listAmount),
          quotedAmount: this.formatAmountOrDash(item.quotedAmount)
        })),
        billingPeriodLabel: this.quote.periodLabel,
        billingPeriodAmount: this.formatMoney(this.quote.billingPeriodAmount),
        serviceItems: this.serviceFeeItems.map((item) => ({
          kind: this.serviceFeeKindLabel(item),
          name: this.quoteLineName(item),
          qty: this.formatQuantity(item),
          listAmount: this.formatAmountOrDash(item.listAmount),
          quotedAmount: this.formatAmountOrDash(item.quotedAmount)
        })),
        serviceFeeTotal: this.formatMoney(this.serviceFeeTotal),
        subtotal: this.formatMoney(this.quote.subtotalExclTax),
        taxLabel: `首年營業稅 ${taxPercent}%`,
        tax: this.formatMoney(this.quote.taxAmount),
        total: this.formatMoney(this.quote.totalInclTax),
        deviatedNote: this.quote.isDeviated
          ? '＊ 標示項目已偏離表價，為業務議價結果；表價另見 LOCKLINE+ 產品定價表（經銷商版）。'
          : '',
        notes: this.form.notes,
        terms: this.catalog.terms || [],
        foot: `計價數量（估價用，有效好友或會員數取高者）：${Number(this.form.effectiveFriends || 0).toLocaleString('zh-TW')} ｜ 方案：${this.selectedPlan?.name || '—'}`
      };
    },
    dealerDocxModel() {
      const share = this.dealerShareResult;
      const mapLine = (item) => ({
        kind: item.kindLabel,
        name: item.note ? `${item.name}\n${item.note}` : item.name,
        basis: this.formatMoneyOrDash(item.basisAmount),
        rate: this.formatShareRate(item.payableRate),
        share: this.formatMoneyOrDash(item.payableAmount)
      });
      return {
        quoteNo: this.savedQuote?.quoteNo || '',
        versionNo: this.savedQuote?.viewingVersion || null,
        customerName: this.form.customerName,
        quoteDate: this.form.quoteDate,
        validUntil: this.form.validUntil,
        catalogVersion: this.catalog.version,
        parameterSummary: share.parameterSummary,
        sellingPriceHeader: `售價（未稅）${this.sellingPeriodSuffix}`,
        subscriptionItems: share.subscriptionLines.map(mapLine),
        subscriptionBasis: this.formatMoney(share.subscriptionBasis),
        subscriptionShare: this.formatMoney(share.subscriptionPayable),
        serviceItems: share.serviceLines.map(mapLine),
        serviceBasis: this.formatMoney(share.serviceBasis),
        serviceShare: this.formatMoney(share.servicePayable),
        firstYearBasis: this.formatMoney(share.firstYearBasis),
        firstYearShare: this.formatMoney(share.firstYearPayable),
        firstYearBasisIncl: this.formatMoney(this.amountInclTax(share.firstYearBasis)),
        firstYearShareIncl: this.formatMoney(this.amountInclTax(share.firstYearPayable)),
        footnotes: share.footnotes,
        notes: this.form.notes,
        foot: `計價數量：${Number(this.form.effectiveFriends || 0).toLocaleString('zh-TW')} ｜ 方案：${this.selectedPlan?.name || '—'}`
      };
    },
    async handleDownloadDocx() {
      if (!this.canPrint || this.docxExporting) return;
      this.docxExporting = true;
      try {
        const { buildDealerShareDocx, buildQuoteDocx } = await import('@/utils/quoteDocx');
        const blob = this.isDealerSheet
          ? await buildDealerShareDocx(this.dealerDocxModel())
          : await buildQuoteDocx(this.quoteDocxModel());
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.quotePdfBaseName()}.docx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error(err);
        window.alert('Word 檔產生失敗，請稍後再試');
      } finally {
        this.docxExporting = false;
      }
    }
  }
};
</script>

<style scoped>
.quote-estimator {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.page-subtitle {
  margin: 6px 0 0;
  color: #6c757d;
  font-size: 13px;
}

.tab-nav {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e5e7eb;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
}

.tab-btn:hover {
  color: #374151;
  background: #f3f4f6;
}

.tab-btn.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
  background: #eff6ff;
}

.preview-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.preview-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.audience-switch {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 240px;
}

.audience-switch__btn {
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 999px;
  cursor: pointer;
}

.audience-switch__btn.active {
  color: #fff;
  background: #1c7ed6;
  border-color: #1c7ed6;
}

.audience-switch__btn:disabled {
  cursor: default;
}

.audience-switch__btn:disabled:not(.active) {
  color: #adb5bd;
  background: #f8f9fa;
}

.audience-switch__hint {
  flex-basis: 100%;
  font-size: 12px;
  color: #868e96;
}

.quote-table .col-rate {
  width: 8em;
  text-align: right;
  white-space: nowrap;
}

.share-rate-input {
  width: 4.6em;
  text-align: right;
}

.share-rate-reset {
  margin-left: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1c7ed6;
  font-size: 12px;
  cursor: pointer;
}

.sub-tab-nav {
  display: flex;
  gap: 8px;
  margin: 0 0 16px;
  padding: 4px;
  background: #f1f3f5;
  border-radius: 8px;
}

.sub-tab-btn {
  flex: 1;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.sub-tab-btn:hover {
  color: #343a40;
  background: #e9ecef;
}

.sub-tab-btn.active {
  color: #1c7ed6;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.tiers-panel,
.saved-panel {
  margin-bottom: 20px;
}

.saved-toolbar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.saved-toolbar .panel-title {
  margin: 0;
}

.saved-toolbar .search-box {
  width: 280px;
}

.saved-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.saved-actions .action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.saved-table th,
.saved-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e9ecef;
  text-align: left;
}

.saved-table th {
  color: #6c757d;
  font-weight: 600;
  background: #f8f9fa;
}

.saved-table .num {
  text-align: right;
  white-space: nowrap;
}

.saved-actions {
  text-align: right;
}

.empty-cell {
  text-align: center;
  color: #868e96;
  padding: 24px 12px;
}

.saved-pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
  font-size: 13px;
}

.version-switch {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.version-switch--compact {
  margin-top: 0;
}

.version-switch__no {
  font-weight: 600;
  color: #343a40;
}

.version-switch__label {
  font-size: 13px;
  color: #6c757d;
}

.version-switch__hint {
  flex-basis: 100%;
  font-size: 12px;
  color: #868e96;
}

.history-alert,
.won-alert {
  border-radius: 6px;
  padding: 8px 12px;
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
}

.history-alert {
  color: #c92a2a;
  background: #fff5f5;
  border: 1px solid #ffc9c9;
}

.won-alert {
  color: #2b8a3e;
  background: #ebfbee;
  border: 1px solid #b2f2bb;
}

.version-switch__hint.history-alert,
.audience-switch__hint.history-alert,
.form-hint.history-alert,
.version-switch__hint.won-alert,
.audience-switch__hint.won-alert,
.form-hint.won-alert {
  flex-basis: 100%;
  display: block;
  margin-top: 8px;
}

.form-fieldset {
  margin: 0;
  padding: 0;
  border: 0;
  min-inline-size: 0;
}

.form-lock-notice {
  margin: 0 0 12px;
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #8a5a00;
  background: #fff8e6;
  border: 1px solid #ffe08a;
  border-radius: 6px;
}

.form-panel.is-locked .form-fieldset {
  opacity: 0.78;
}

.version-chip {
  padding: 4px 10px;
  font-size: 13px;
  line-height: 1.4;
  color: #495057;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 999px;
  cursor: pointer;
}

.version-chip:hover:not(.active) {
  background: #f1f3f5;
}

.version-chip.active {
  color: #fff;
  background: #1c7ed6;
  border-color: #1c7ed6;
}

.version-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 8px 0 16px;
}

.tiers-intro,
.tiers-note {
  margin: 0 0 12px;
  font-size: 13px;
  color: #6c757d;
  line-height: 1.5;
}

.tiers-note {
  margin: 12px 0 0;
}

.table-wrapper {
  overflow-x: auto;
}

.tiers-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  min-width: 640px;
}

.tiers-table th,
.tiers-table td {
  border: 1px solid #dee2e6;
  padding: 10px 12px;
}

.tiers-table th {
  background: #f8f9fa;
  font-weight: 600;
  text-align: left;
}

.tiers-table .num {
  text-align: right;
  white-space: nowrap;
}

.badge-won {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #d3f9d8;
  color: #2b8a3e;
  font-size: 12px;
  font-weight: 600;
}

.badge-deviated {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #fff3cd;
  color: #856404;
  font-size: 12px;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.estimator-layout {
  display: block;
}

.panel {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px 20px 20px;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  margin: 20px 0 12px;
}

.panel-title:first-child {
  margin-top: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.deal-mode-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group--full {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.form-hint {
  margin: 0;
  font-size: 12px;
  color: #868e96;
}

.form-hint-error {
  color: #c92a2a;
}

.is-invalid {
  border-color: #c92a2a;
}

.tax-id-input {
  ime-mode: disabled;
}

.custom-item {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.4fr) 88px 120px auto;
  gap: 10px;
  align-items: end;
  margin-top: 12px;
}

.custom-item__side {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 2px;
}

.custom-item__sum {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.custom-item-add {
  margin-top: 12px;
}

.checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
}

.checkbox-item--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.module-hint {
  margin: -4px 0 10px;
}

.quote-amount-cell {
  min-width: 120px;
}

.quote-inline-input {
  width: 100%;
  max-width: 140px;
  margin-left: auto;
  padding: 4px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  text-align: right;
  font-size: 13px;
}

.print-only {
  display: none;
}

.alert {
  margin: 12px 0;
  padding: 12px;
  border-radius: 6px;
  font-size: 13px;
}

.alert p {
  margin: 0 0 8px;
}

.alert-warning {
  background: #fff8e6;
  border: 1px solid #ffe08a;
  color: #7a5b00;
}

.alert-info {
  background: #eef5ff;
  border: 1px solid #b6d0ff;
  color: #1a4b8c;
}

.summary-list {
  margin: 0;
}

.summary-list > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f5;
  font-size: 14px;
}

.summary-list dt {
  margin: 0;
  color: #6c757d;
  font-weight: 400;
}

.summary-list dd {
  margin: 0;
  font-weight: 600;
}

.summary-total {
  border-bottom: none !important;
  margin-top: 4px;
  padding-top: 12px !important;
  font-size: 16px !important;
}

.summary-renewal {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed #dee2e6;
}

.renewal-hint {
  margin: 10px 0 0;
  font-size: 12px;
  color: #868e96;
  line-height: 1.45;
}

.enterprise-base-hint {
  margin: 0 0 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f3f5;
}

.print-hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: #adb5bd;
}

.quote-preview {
  max-width: 100%;
  overflow-x: auto;
}

.quote-sheet {
  margin-top: 16px;
  padding: 24px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #212529;
  box-sizing: border-box;
  max-width: 100%;
}

.quote-sheet__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #212529;
}

.quote-sheet__brand {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.quote-sheet__title {
  margin: 4px 0 0;
  font-size: 22px;
  font-weight: 600;
}

.quote-sheet__meta {
  text-align: right;
  font-size: 13px;
}

.quote-sheet__meta p {
  margin: 0 0 4px;
}

.quote-sheet__parties {
  margin-bottom: 20px;
  font-size: 13px;
}

.quote-sheet__parties h3 {
  margin: 0 0 8px;
  font-size: 13px;
  color: #6c757d;
  font-weight: 600;
}

.quote-sheet__customer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 24px;
}

.quote-sheet__customer-col p {
  margin: 0 0 4px;
}

.quote-sheet__salesperson {
  margin: 10px 0 0;
  padding-top: 8px;
  border-top: 1px solid #e9ecef;
}

.quote-table-frame {
  border: 1px solid #adb5bd;
  margin-bottom: 12px;
}

.quote-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 0;
}

.quote-table .col-kind {
  width: 5.5em;
}

.quote-table .col-desc {
  width: auto;
}

.quote-table .col-qty {
  width: 4.2em;
}

.quote-table .col-money {
  width: 8.5em;
  min-width: 8.5em;
}

.quote-table th,
.quote-table td {
  border: 1px solid #ced4da;
  padding: 8px 10px;
  vertical-align: top;
}

.quote-table th {
  background: #f8f9fa;
  font-weight: 600;
}

.quote-table .col-kind,
.quote-table th.col-kind,
.quote-table td.col-kind {
  white-space: nowrap;
  word-break: keep-all;
}

.quote-table .col-qty,
.quote-table th.col-qty,
.quote-table td.col-qty,
.quote-table .num {
  text-align: right;
  white-space: nowrap;
  word-break: keep-all;
}

.quote-table td.col-money,
.quote-table th.col-money,
.quote-table tfoot td.num {
  overflow: visible;
  white-space: nowrap;
  word-break: keep-all;
}

.quote-table .col-qty {
  padding-left: 6px;
  padding-right: 8px;
}

.quote-table .row-sub td {
  background: #f8f9fa;
  font-weight: 500;
}

.renewal-ref-note {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 400;
  color: #868e96;
}

.quote-table tfoot td {
  font-weight: 500;
}

.quote-table .first-year-summary td {
  font-weight: 700;
}

.quote-table .total-row td {
  font-weight: 700;
  background: #f1f3f5;
}

.quote-table .grand-total-row td {
  font-size: 16px;
  font-weight: 700;
  padding-top: 10px;
  padding-bottom: 10px;
}

.deviated-mark {
  color: #c92a2a;
  font-weight: 700;
}

.deviated-note,
.quote-foot {
  font-size: 12px;
  color: #495057;
}

.quote-notes,
.quote-terms {
  margin-top: 16px;
  font-size: 12px;
}

.quote-notes h3,
.quote-terms h3 {
  margin: 0 0 8px;
  font-size: 13px;
}

.quote-terms ol {
  margin: 0;
  padding-left: 1.2rem;
}

.quote-terms li {
  margin-bottom: 4px;
}

@media (max-width: 960px) {
  .estimator-layout {
    grid-template-columns: 1fr;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .custom-item {
    grid-template-columns: 1fr 1fr;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .print-only {
    display: inline !important;
  }

  .quote-estimator {
    padding: 0;
    overflow: visible !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .quote-estimator > :not(.estimator-layout) {
    display: none !important;
  }

  .form-panel > :not(.quote-preview) {
    display: none !important;
  }

  .form-panel {
    border: none !important;
    padding: 0 !important;
    background: transparent !important;
  }

  .quote-sheet {
    margin: 0;
    border: none;
    border-radius: 0;
    padding: 0 1px 0 0;
    overflow: hidden !important;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }

  .quote-table-frame {
    border: 1px solid #495057 !important;
    box-sizing: border-box;
    width: 100%;
    overflow: hidden;
  }

  .quote-table {
    table-layout: fixed;
    width: 100% !important;
    max-width: 100% !important;
    border-collapse: collapse;
  }

  /* 避免 tfoot 在每頁底部重印，統計列只出現一次 */
  .quote-table tfoot {
    display: table-row-group !important;
  }

  .quote-table .col-kind {
    width: 12% !important;
  }

  .quote-table .col-qty,
  .quote-table .col-rate {
    width: 8% !important;
  }

  .quote-table .col-money {
    width: 18% !important;
    min-width: 0 !important;
    max-width: 18% !important;
  }

  .quote-table th,
  .quote-table td {
    padding: 6px 6px !important;
  }

  .quote-table td.col-money,
  .quote-table th.col-money,
  .quote-table tfoot td.num {
    overflow: hidden !important;
    white-space: nowrap !important;
  }

  .quote-table th:last-child,
  .quote-table td:last-child {
    border-right: 1px solid #495057 !important;
  }

  .quote-table .grand-total-row td {
    font-size: 16px !important;
  }

  .quote-table .col-kind,
  .quote-table .col-qty,
  .quote-table .col-rate,
  .quote-table .col-money,
  .quote-table .num {
    white-space: nowrap !important;
    word-break: keep-all !important;
  }

  .table-wrapper {
    overflow: visible !important;
  }
}
</style>

<style>
@media print {
  @page {
    size: A4;
    margin: 12mm;
  }

  html,
  body {
    width: 100% !important;
    height: auto !important;
    overflow: visible !important;
  }

  .sidebar,
  .sidebar-overlay,
  .topbar,
  .subscription-alert,
  .help-window {
    display: none !important;
  }

  .main-layout,
  .main-content,
  .page-content {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: none !important;
    overflow: visible !important;
    height: auto !important;
  }
}
</style>
